import { Permissions } from './../../../core/constants/permissions';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BalanceThresholds } from '../../trial/trial-details/trial-payments.model';
import {
  SetSubsidiaryBalanceAlertsRequest,
  SubsidiaryPaymentDetailsViewModel
} from "../../../core/models/caxton.models";
import { CaxtonService } from "../../../core/services/caxton.service";
import { CaxtonCardService } from "../../../core/services/caxton-card.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { AuthService } from "../../../core/services/auth.service";
import { TemplateService } from "../../../core/services/template.service";
import { ModalComponent } from "../../../shared/modal/modal.component";
import { ModalV2Component } from "../../../shared/modal-v2/modal-v2.component";
import { LogHelper } from "../../../core/helpers/log.helper";
import { environment } from "../../../../environments/environment";

@Component({
  selector: 'app-parent-subsidiary-details',
  templateUrl: './parent-subsidiary-details.component.html',
  styleUrls: ['./parent-subsidiary-details.component.scss']
})
export class ParentSubsidiaryDetailsComponent implements OnInit {
  @Input() subsidiaryId: string;
  subsidiaryPaymentDetailsViewModel: SubsidiaryPaymentDetailsViewModel;
  textConnectionIsProcessing = false;
  @ViewChild('alertsModal') alertsModal: ModalComponent;
  @ViewChild('archiveEmptyWalletsModal') archiveEmptyWalletsModal: ModalV2Component;
  @ViewChild('noAlertEmailModal') noAlertEmailModal: ModalV2Component;
  balanceThresholds: BalanceThresholds[];
  alertForm: UntypedFormGroup;
  archiveEmptyWalletsForm: FormGroup<{ dryRun: FormControl<boolean> }>;
  alertUpdateProcessing: boolean = false;
  archivingEmptyWallets: boolean = false;
  saveProcessing: boolean = false;
  Permissions = Permissions;
  selectedTab = 'caxton';
  isDevelopmentEnv: boolean = false;
  creatingTestWallet: boolean = false;

  constructor(
    private readonly alertService: AlertService,
    public authService: AuthService,
    private readonly caxtonService: CaxtonService,
    private readonly caxtonCardService: CaxtonCardService,
    private readonly templateService: TemplateService) { }

  ngOnInit(): void {
    this.templateService.showHeader();

    this.caxtonService.getParentSubsidiary().subscribe({
      next: response => {
        this.subsidiaryPaymentDetailsViewModel = response;
      },
      error: (error) => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Unable to retrieve trial payments info');
      }
    });

    this.alertForm = new UntypedFormGroup({
      alertEmail: new UntypedFormControl('', [Validators.required, Validators.email])
    });

    this.archiveEmptyWalletsForm = new FormGroup({
      dryRun: new FormControl<boolean>(true, { nonNullable: true })
    });

    if (environment.environment === 'DEVELOPMENT' || environment.environment === 'STAGING') {
      this.isDevelopmentEnv = true;
    }
  }

  onSelectTab(tabName: string) {
    this.selectedTab = tabName;
  }

  testConnectionToCaxton() {
    this.textConnectionIsProcessing = true;
    this.caxtonService.getParentSubsidiary().subscribe({
      next: response => {
        this.alertService.showSuccessAlert('Refresh successful');
        this.subsidiaryPaymentDetailsViewModel = response;
        this.textConnectionIsProcessing = false;
      },
      error: (error) => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Connection to Caxton failed');
        this.textConnectionIsProcessing = false;
      }
    });
  }

  getBalanceAlerts() {
    this.alertsModal.show();
    this.caxtonService.getBalanceAlerts().subscribe(result => {
      this.balanceThresholds = result.balanceThresholds;
      this.alertForm.controls.alertEmail.setValue(result.alertEmail);
      result.balanceThresholds.forEach(t => {
        this.alertForm.addControl(t.currency, new UntypedFormControl(t.balance, Validators.min(0)));
      });
    });
  }

  updateAlerts() {
    this.alertUpdateProcessing = true;
    const request: SetSubsidiaryBalanceAlertsRequest = {
      alertEmail: this.alertForm.get('alertEmail').value,
      currencyThresholds: []
    };

    Object.keys(this.alertForm.controls).forEach(key => {
      if (key !== 'alertEmail') {
        request.currencyThresholds.push({
          currency: key,
          balance: this.alertForm.get(key).value
        });
      }
    });

    this.caxtonService.updateBalanceAlerts(request).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Alerts successfully set.');
        this.alertsModal.hide();
        this.alertUpdateProcessing = false;
      },
      error: () => {
        this.alertService.showWarningAlert('Failed to set alerts. Please try again or contact support');
        this.alertUpdateProcessing = false;
      }
    });
  }

  showArchiveEmptyWalletsModal() {
    this.archiveEmptyWalletsForm.reset({ dryRun: true });
    this.archiveEmptyWalletsModal.show();
  }

  archiveEmptyWallets() {
    const alertEmail = this.subsidiaryPaymentDetailsViewModel.walletSweepAlertEmail;

    if (!alertEmail)
    {
      this.archiveEmptyWalletsModal.hide();
      this.noAlertEmailModal.show();
      return;
    }

    this.archivingEmptyWallets = true;
    const dryRun = this.archiveEmptyWalletsForm.controls.dryRun.value;

    this.caxtonCardService.archiveEmptyWallets(dryRun).subscribe({
      next: () => {
        this.archivingEmptyWallets = false;
        this.archiveEmptyWalletsModal.hide();
        this.alertService.showSuccessAlert(`Wallet archive sweep started. A report will be emailed to ${alertEmail} once it completes.`);
      },
      error: (error) => {
        this.archivingEmptyWallets = false;
        LogHelper.log(error);
        this.alertService.showWarningAlert(error.error ? error.error.title : 'Unable to start wallet archive sweep. Please try again.');
      }
    });
  }

  createTestWalletOnParentSubsidiary(walletName: string) {
    if (!walletName) {
      this.alertService.showWarningAlert('Please enter a wallet name.');
      return;
    }

    this.creatingTestWallet = true;

    this.caxtonCardService.createTestWalletOnParentSubsidiary(walletName).subscribe({
      next: (result) => {
        this.creatingTestWallet = false;
        this.alertService.showSuccessAlert(`Created empty wallet "${result.walletName}" (id ${result.walletId}) on the parent subsidiary.`);
      },
      error: (error) => {
        this.creatingTestWallet = false;
        LogHelper.log(error);
        this.alertService.showWarningAlert(error.error ? error.error.title : 'Unable to create test wallet. Please try again.');
      }
    });
  }
}
